import type { Problem } from "@/lib/api/types";
import { cn } from "@/lib/utils";

/**
 * The three states every contract-backed surface must render, in the Counter world.
 *
 * Loading is a quiet placeholder block — no shimmer, no spinner. A counter screen that pulses at a
 * cashier all day is a tax on attention.
 *
 * Errors are HELPER-TEXT scale by explicit product rule: no filled alert banners. Severity is
 * carried by the error role, and the message always pairs with the server's `recovery` when it
 * sends one, because "what do I do now" is the only useful part of an error at a till.
 */

const WIDTH = ["w-full", "w-11/12", "w-4/5"];

/** Placeholder block. Give it the rough shape of what is loading. */
export function LoadingBlock({
  className,
  lines = 1,
  label = "Loading",
}: {
  className?: string;
  lines?: number;
  label?: string;
}) {
  return (
    <div role="status" aria-busy="true" aria-label={label} className={cn("flex flex-col gap-2", className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className={cn("h-[14px] rounded-control border border-stroke-weak/70 bg-fill-neutral-weak/70", WIDTH[i % 3])}
        />
      ))}
      <span className="sr-only">{label}…</span>
    </div>
  );
}

/**
 * A server `Problem`, rendered at helper-text scale. Shows `recovery` when present — never a
 * bare error code, which tells a cashier nothing.
 */
export function ProblemNote({
  problem,
  onRetry,
  className,
}: {
  problem: Problem;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <p role="alert" className={cn("flex flex-wrap items-baseline gap-x-2 text-sm leading-relaxed", className)}>
      <span className="font-semibold text-text-error">{problem.message}</span>
      {problem.recovery && <span className="text-text-weak">{problem.recovery}</span>}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="font-semibold text-text-strong underline underline-offset-2 hover:text-text-weak"
        >
          Try again
        </button>
      )}
    </p>
  );
}

/** One honest line for an empty surface, plus an optional next action. */
export function EmptyNote({
  children,
  action,
  className,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-start gap-2 py-8", className)}>
      {/* A div, not a <p>: screens legitimately pass a button or a chip as the message, and a
          <button> inside a <p> is invalid markup that React will not warn about until hydration. */}
      <div className="text-sm text-text-disabled">{children}</div>
      {action}
    </div>
  );
}
